/**
 * "This page was opened from inside the iPhone app."
 *
 * kev-mobile has no sign-up form of its own. Creating an account opens
 * `/signup` in an in-app browser sheet (SFSafariViewController), which is a
 * real browser showing a real page of this site — chrome, nav and all.
 *
 * ⚠️ WHY THIS EXISTS AT ALL: APP STORE GUIDELINE 3.1.1. An iOS app may not
 * contain buttons, links or calls to action that send a reader to a purchasing
 * mechanism other than in-app purchase. kev-mobile sells nothing and links to
 * nothing — but the page it opens rendered the signed-out marketing bar, whose
 * third item is Pricing, and from there it is one tap to the plans and another
 * to Stripe. The app's own App Store review notes state in writing that it
 * "contains no links, prices or calls to action for any external purchase", so
 * the funnel showing up inside an app-opened sheet made that sentence false as
 * well as risky.
 *
 * The signed-IN bar was never the problem (Fixtures · Insights · Fantasy ·
 * Account — "those three are the sales funnel, and someone signed in is already
 * past it", see Nav). This only has to answer for the signed-out one.
 *
 * ⚠️ NOT A SECURITY BOUNDARY, and not a paywall. Anyone can put `?app=1` on a
 * URL and see the same reduced bar. That is fine: this hides a marketing
 * funnel from one surface, it does not protect anything. Never gate content,
 * entitlements or data on it.
 */

const KEY = "kev.appContext";
const PARAM = "app";

/**
 * Read once and remembered for the tab's session.
 *
 * The param only rides the FIRST url. React Router navigations inside the sheet
 * drop the query string, so a reader who lands on `/signup?app=1` and moves a
 * page would get the funnel back if this re-read `location.search` every time.
 * `sessionStorage` is the right lifetime: it dies with the sheet, so it can
 * never leak into a normal browser tab on the same machine.
 *
 * Wrapped because storage throws rather than returning null in a locked-down
 * browser (Safari private mode, blocked site data) — and the honest answer when
 * we cannot tell is "no", which shows the ordinary site.
 */
export function isAppContext(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(PARAM) === "1") {
      sessionStorage.setItem(KEY, "1");
      return true;
    }
    return sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}
